import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLocation } from "react-router-dom";

// Layouts & Components
import LandingLayout from './layouts/LandingLayout';
import DashboardLayout from './layouts/DashboardLayout';
import SelectionLayout from './layouts/SelectionLayout';
import ProtectedRoute from './components/common/ProtectedRoute';
import { onAuthStateChange } from './services/auth';

// Lazy Loaded Pages
const Home = lazy(() => import('./pages/landing/Home'));
const Features = lazy(() => import('./pages/landing/Features'));
const About = lazy(() => import('./pages/landing/About'));
const Contact = lazy(() => import('./pages/landing/Contact'));
const Pricing = lazy(() => import('./pages/landing/Pricing'));
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const LandSelection = lazy(() => import('./pages/selection/LandSelection'));
const CropSelection = lazy(() => import('./pages/selection/CropSelection'));
const Overview = lazy(() => import('./pages/dashboard/Overview'));
const Expenses = lazy(() => import('./pages/dashboard/Expenses'));
const Labor = lazy(() => import('./pages/dashboard/Labor'));
const Chemicals = lazy(() => import('./pages/dashboard/Chemicals'));
const SprayCycles = lazy(() => import('./pages/dashboard/SprayCycles'));
const Irrigation = lazy(() => import('./pages/dashboard/Irrigation'));
const Notifications = lazy(() => import('./pages/dashboard/Notifications'));
const Account = lazy(() => import('./pages/dashboard/Account'));
const Settings = lazy(() => import('./pages/dashboard/Settings'));
const Reports = lazy(() => import('./pages/dashboard/Reports'));
const Income = lazy(() => import('./pages/dashboard/Income'));
const Suggestions = lazy(() => import('./pages/dashboard/Suggestions'));
const AIChat = lazy(() => import('./pages/dashboard/AIChat'));
const Machines = lazy(() => import('./pages/dashboard/Machines'));
const Maintenance = lazy(() => import('./pages/dashboard/Maintenance'));


function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [assetsReady, setAssetsReady] = useState(false);
  const [loading, setLoading] = useState(true);

  // 1. التاكد من المصادقة
  useEffect(() => {
    const unsubscribe = onAuthStateChange((authUser) => {
      setUser(authUser);
      setAuthChecked(true);
    });
    return () => { if (unsubscribe) unsubscribe(); };
  }, []);

  // 2. التاكد من تحميل الفونتات والايقونات بالكامل
  useEffect(() => {
    // الانتظار حتى تحميل جميع الخطوط (بما فيها الايقونات)
    document.fonts.ready.then(() => {
      // اضافة تاخير بسيط 300 مللي ثانية للتاكد من استقرار الـ DOM
      setTimeout(() => {
        setAssetsReady(true);
      }, 300);
    });
  }, []);

  // 3. دمج حالات التحميل وفك قفل الاسكرول
  useEffect(() => {
    if (authChecked && assetsReady) {
      // تقليل شاشة التحميل تدريجياً
      setTimeout(() => {
        setLoading(false);
      }, 200);
    }
  }, [authChecked, assetsReady]);

  // تسطيب كلاريتي لتحليلات المستخدمين
  useEffect(() => {
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) };
      t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", "vyfk3yxday");
  }, []);

  const location = useLocation();

  useEffect(() => {
    if (window.clarity) {
      window.clarity("set", "page", location.pathname);
    }
  }, [location]);

  if (loading) {
    return (
      <div className="initial-loader-v2">
        <div className="loader-content">
          <img src="/img/logo.jpeg" alt="ديوان الأرض" className="loader-logo" />
          <div className="spinner"></div>
          <div className="loading-text">ديوان الأرض.. جاري التحقق من الهوية</div>
        </div>
      </div>
    );
  }

  const LoadingFallback = () => (
    <div className="initial-loader-v2">
      <div className="loader-content">
        <div className="spinner"></div>
        <div className="loading-text">جاري تحضير المحرك...</div>
      </div>
    </div>
  );


  return (
    <>
      <Helmet>
        {/* الأساسيات */}
        <title>ديوان الأرض | منصة إدارة المزارع الذكية</title>
        <meta name="description" content="ديوان الأرض منصة ذكية لإدارة المزارع، متابعة المحاصيل، حساب التكاليف والأرباح، وتحسين الإنتاج الزراعي بسهولة." />
        <meta name="keywords" content="
إدارة مزارع, برنامج إدارة مزارع, نظام إدارة المزارع, إدارة الأراضي الزراعية,
حساب تكاليف الزراعة, حساب أرباح المحاصيل, متابعة المصاريف الزراعية,
تسجيل المصاريف الزراعية, دفتر حسابات المزرعة, برنامج حسابات زراعي,
إدارة الري, نظام الري الذكي, متابعة الري, جداول الري,
إدارة المحاصيل, متابعة نمو المحاصيل, إدارة زراعة القمح, زراعة الطماطم, زراعة البطاطس,
حساب تكلفة الفدان, أرباح الفدان, إنتاجية المحاصيل,
برنامج للفلاح, تطبيق للفلاح, إدارة المزرعة من الموبايل,
متابعة العمالة الزراعية, حساب أجور العمال, إدارة العمال في المزرعة,
إدارة الأسمدة والمبيدات, تسجيل الكيماويات الزراعية, حساب تكلفة الرش,
متابعة دورات الرش, برنامج زراعة حديث, زراعة ذكية في مصر,نظام ادارة المزارع الذكية, تطبيق الزراعة الذكية, برنامج الزراعة الذكية, تكنولوجيا الزراعة, التحول الرقمي في الزراعة, farm management system, smart agriculture, crop tracking, irrigation management, agriculture app, farm accounting software, أفضل برنامج لإدارة المزارع في مصر, تكنولوجيا الزراعة, التحول الرقمي في الزراعة,
أفضل برنامج لإدارة المزارع في مصر, تكنولوجيا الزراعة, التحول الرقمي في الزراعة,
إدارة المزارع, برنامج إدارة مزارع, نظام إدارة المزارع, إدارة الأراضي الزراعية,
حساب تكاليف الزراعة, حساب أرباح المحاصيل, متابعة المصاريف الزراعية,
تسجيل المصاريف الزراعية, دفتر حسابات المزرعة, برنامج حسابات زراعي,
إدارة الري, نظام الري الذكي, متابعة الري, جداول الري,
إدارة المحاصيل, متابعة نمو المحاصيل, إدارة زراعة القمح, زراعة الطماطم, زراعة البطاطس,
حساب تكلفة الفدان, أرباح الفدان, إنتاجية المحاصيل,
برنامج للفلاح, تطبيق للفلاح, إدارة المزرعة من الموبايل,
متابعة العمالة الزراعية, حساب أجور العمال, إدارة العمال في المزرعة,
إدارة الأسمدة والمبيدات, تسجيل الكيماويات الزراعية, حساب تكلفة الرش,
متابعة دورات الرش, برنامج زراعة حديث, زراعة ذكية في مصرو,نظام ادارة المزارع الذكية, تطبيق الزراعة الذكية, برنامج الزراعة الذكية, تكنولوجيا الزراعة, التحول الرقمي في الزراعة,برمجة محمد سلامة , فكرة ومحتوى ومسؤول عن التطوير والإدارة الرقمية: عبدالله جابر و محمد سلامة, mohamed salama, abdullah jaber, diwan alard, ديون الأرض, ديوان الأرض, ديوان الارض, ديوان الارض, diwan alard, نظام ادارة اراضي, ديون الارض, ديون الأرض, ديون الأرض
"/>

        <meta name="author" content="Mohamed Salama" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="Arabic" />

        {/* Viewport */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        {/* Open Graph (فيسبوك) */}
        <meta property="og:title" content="ديوان الأرض | منصة إدارة المزارع الذكية" />
        <meta property="og:description" content="نظام متكامل لإدارة المزارع، متابعة المحاصيل، التكاليف، والأرباح في مكان واحد." />
        <meta property="og:image" content="https://dewan-alard.vercel.app/img/logo.jpeg" />
        <meta property="og:url" content="https://dewan-alard.vercel.app" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="ar_AR" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ديوان الأرض | إدارة المزارع بسهولة" />
        <meta name="twitter:description" content="تابع محاصيلك واحسب أرباحك بكل سهولة باستخدام ديوان الأرض." />
        <meta name="twitter:image" content="https://dewan-alard.vercel.app/img/logo.jpeg" />

        {/* Canonical */}
        <link rel="canonical" href="https://dewan-alard.vercel.app/" />

        {/* Favicon */}
        <link rel="icon" href="/img/logo.jpeg" />

        {/* Theme Color */}
        <meta name="theme-color" content="#2e7d32" />

        {/* Apple Meta */}
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />

        {/* Structured Data (SEO جامد) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "ديوان الأرض",
            "operatingSystem": "Web",
            "applicationCategory": "BusinessApplication",
            "description": "برنامج إدارة مزارع يساعد على متابعة المحاصيل وحساب التكاليف والأرباح وإدارة الري والعمالة.",
            "url": "https://dewan-alard.vercel.app",
            "inLanguage": "ar",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "EGP"
            }
          })}
        </script>
      </Helmet>

      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* 1. صفحات الهبوط */}
          <Route element={<LandingLayout user={user} />}>
            <Route path="/" element={<Home />} />
            <Route path="/features" element={<Features />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pricing" element={<Pricing />} />
          </Route>

          {/* 2. صفحات تسجيل الدخول */}
          <Route path="/login" element={user ? <Navigate to="/select-land" replace /> : <Login />} />
          <Route path="/register" element={user ? <Navigate to="/select-land" replace /> : <Register />} />

          {/* 3. صفحات الاختيار العامة */}
          <Route
            element={
              <ProtectedRoute user={user}>
                <SelectionLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/select-land" element={<LandSelection />} />
            <Route path="/land/:landId/select-crop" element={<CropSelection />} />
            <Route path="/account" element={<Account />} />
          </Route>

          {/* 4. لوحة التحكم (الداشبورد المختص بأرض وزرعة معينة) */}
          <Route
            path="/dashboard/land/:landId/crop/:cropId"
            element={
              <ProtectedRoute user={user}>
                <DashboardLayout user={user} />
              </ProtectedRoute>
            }
          >
            <Route index element={<Overview />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="labor" element={<Labor />} />
            <Route path="income" element={<Income />} />
            <Route path="chemicals" element={<Chemicals />} />
            <Route path="spray-cycles" element={<SprayCycles />} />
            <Route path="irrigation" element={<Irrigation />} />
            <Route path="suggestions" element={<Suggestions />} />
            <Route path="ai-chat" element={<AIChat />} />
            <Route path="machines" element={<Machines />} />
            <Route path="maintenance" element={<Maintenance />} />
            <Route path="reports" element={<Reports />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="settings" element={<Settings />} />
            <Route path="account" element={<Account />} />
          </Route>

          {/* تحويل أي رابط غير معروف */}
          <Route path="*" element={<Navigate to={user ? "/select-land" : "/"} replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;